// src/hrms/alarms/alarmTypes.ts
/**
 * Alarm-based reminders for Daily Planner todos and tasks (web).
 *
 * An alarm is NOT the planner's `reminder_at`. That field is a plain reminder
 * the server stores; an alarm rings out loud, re-rings every few minutes while
 * it is ignored, and waits for an answer (Complete / Snooze / Remind me again /
 * Open / Dismiss).
 *
 * The shape matches the TeamLio mobile app's alarms (src/features/alarms) so a
 * later server sync can carry one record between the two. The difference is
 * who rings it: a phone hands every alert to the OS, while a browser can only
 * ring while a TeamLio tab is open — nothing can wake a closed browser or a
 * locked device. Alerts that fall in such a gap surface as "missed".
 */

export type AlarmItemSource = "todo" | "task";
export type AlarmItemKey = `${AlarmItemSource}:${string}`;

/** "system" is the phone's own alarm tone (Android app only); the web plays the two bundled ones. */
export type AlarmSound = "system" | "classic" | "chime";

/** One configured alert: either relative to the item's due time or a fixed instant. */
export type AlarmAlert =
  | { id: string; kind: "offset"; minutesBefore: number }
  | { at: string; id: string; kind: "at" };

/** What the alarm needs to describe the item when the page that listed it is not open. */
export type AlarmItemSnapshot = {
  description?: string | null;
  /** Local ISO date-time (no zone) the item is due, or null when it has no due date. */
  dueAt: string | null;
  id: string;
  priority: string;
  source: AlarmItemSource;
  title: string;
};

export type AlarmHandledAction = "completed" | "dismissed" | "snoozed" | "opened" | "missed";

/** An extra one-off alert created by Snooze or Remind me again. */
export type AlarmFollowUp = { at: number; id: string; reason: "snooze" | "remind" };

export type ItemAlarm = {
  alerts: AlarmAlert[];
  /**
   * When the alarm was last switched on, edited or had its due time moved.
   * An alert that was already in the past at that moment is not "missed" —
   * the user never expected it to ring.
   */
  armedAt: number;
  /** Set when the item is finished; no alert of a closed alarm ever rings again. */
  closed?: "completed" | "deleted" | "cancelled";
  enabled: boolean;
  followUps: AlarmFollowUp[];
  /** Occurrence id -> what the user did about it. An occurrence never rings once handled. */
  handled: Record<string, { action: AlarmHandledAction; at: number }>;
  item: AlarmItemSnapshot;
  /** "Complete" pressed while the server could not be reached; retried when the browser is back online. */
  pendingCompletion?: boolean;
  repeatEveryMinutes: AlarmRepeatInterval;
  snoozeMinutes: number;
  sound: AlarmSound;
  /** Kept for parity with the mobile record; a desktop browser cannot vibrate. */
  vibrate: boolean;
};

export type AlarmRepeatInterval = 1 | 2 | 5 | 10;

/** Alarm settings the forms edit; the rest of ItemAlarm is runtime state. */
export type AlarmSettings = Pick<ItemAlarm, "alerts" | "enabled" | "repeatEveryMinutes" | "snoozeMinutes" | "sound" | "vibrate">;

export const REPEAT_INTERVALS: AlarmRepeatInterval[] = [1, 2, 5, 10];
export const SNOOZE_OPTIONS = [5, 10, 15, 30];
export const ADVANCE_OPTIONS = [0, 5, 15, 30, 60, 120, 1440];
/** "Remind me again" choices in the alarm dialog, in minutes from now. */
export const REMIND_AGAIN_OPTIONS = [15, 60, 180];

/** How many times an ignored alert re-rings after the first one (same as mobile). */
export const ALARM_REPEAT_COUNT = 6;
/** Longest one ring keeps sounding before the next repeat takes over. */
export const ALARM_RING_WINDOW_MS = 5 * 60_000;
/** A date-only due date is treated as due at this local time. */
export const DEFAULT_DUE_TIME = "09:00";

export function alarmItemKey(source: AlarmItemSource, id: string): AlarmItemKey {
  return `${source}:${id}`;
}

export function itemIdOf(itemKey: AlarmItemKey) {
  return itemKey.slice(itemKey.indexOf(":") + 1);
}

export function minutesLabel(minutes: number) {
  if (minutes === 0) return "At due time";
  if (minutes % 1440 === 0) return minutes === 1440 ? "1 day before" : `${minutes / 1440} days before`;
  if (minutes % 60 === 0) return minutes === 60 ? "1 hour before" : `${minutes / 60} hours before`;
  return `${minutes} min before`;
}

export function durationLabel(minutes: number) {
  if (minutes % 60 === 0) return minutes === 60 ? "1 hour" : `${minutes / 60} hours`;
  return minutes === 1 ? "1 minute" : `${minutes} minutes`;
}
